import { StepForms } from '@/stores/slices/entity-slice';

import { TeamAssociateStatusType } from '../types';

export interface GetEntityTeamParams {
  id: string;
  page?: number;
  limit?: number;
}

export interface GetEntityTeamRes {
  items: EntityTeamItem[];
  total: number;
  page: number;
  limit: number;
}

export interface EntityTeamItem {
  name: string | null;
  alias: string | null;
  email: string;
  userId: string | null;
  entityMemberId: string | null;
  entityInvitationId: string | null;
  role: EntityRole;
  isDirector: boolean;
  fundNames: string[];
  status: TeamAssociateStatusType;
  fundAssociations: EntityTeamItemFundAssociations[];
  legalRoleTransitionStatus?: LegalRoleTransitionStatus;
}

export interface EntityTeamItemFundAssociations {
  fundName: string | null;
  fundMemberId: string | null;
  fundInvitationId: string | null;
  fundId: string;
  title: string | null;
  memberSince: string | null;
  type: EntityTeamAssociateType;
  fundApproved: boolean | null;
  slug: string | null;
}

export const entityRole = {
  Viewer: 'Viewer',
  Initiator: 'Initiator',
  Approver: 'Approver',
  Admin: 'Admin',
} as const;

export type EntityRole = keyof typeof entityRole;

export const entityTeamAssociateType = {
  Member: 'Member',
  NotAssociate: 'NotAssociate',
  FundInvitation: 'FundInvitation',
} as const;

export type EntityTeamAssociateType = keyof typeof entityTeamAssociateType;

export const entityTeamLegalRole = {
  Director: 'Director',
  TeamMember: 'TeamMember',
} as const;

export type EntityTeamLegalRole = keyof typeof entityTeamLegalRole;

export const legalRoleTransitionStatus = {
  DirectorApproved: 'DirectorApproved',
  TeamMemberApproved: 'TeamMemberApproved',
  DirectorPending: 'DirectorPending',
  FromTeamMemberToDirectorPending: 'FromTeamMemberToDirectorPending',
  FromDirectorToTeamMemberPending: 'FromDirectorToTeamMemberPending',
} as const;

export type LegalRoleTransitionStatus = keyof typeof legalRoleTransitionStatus;

export const entityApprovalStatus = {
  Verified: 'Verified',
  ReVerificationPending: 'ReVerificationPending',
  AwaitingApproval: 'AwaitingApproval',
  Blocked: 'Blocked',
  Rejected: 'Rejected',
  Draft: 'Draft',
} as const;

export type EntityApprovalStatus = keyof typeof entityApprovalStatus;

export const versionOption = {
  Published: 'published',
  Draft: 'draft',
} as const;

export type VersionOption = (typeof versionOption)[keyof typeof versionOption];

export type GetSingleEntityRes = StepForms & {
  entityApprovalStatus: EntityApprovalStatus;
  isProvider: boolean;
  blockedAt?: string | null;
  blockReason?: string | null;
  blockInternalReference?: string | null;
  directorsUsersIdsApproved?: string[];
  directorsUsersIdsInDraft?: string[];
};

export interface GetEntityPublicRes {
  id: string;
  legalName: string | null;
  displayName: string | null;
  country: string | null;
  state: string | null;
  type: string | null;
  registeredAt: string | null;
  logoImageId: string | null;
  logoUrl: string | null;
  entityEmail: string | null;
  entityWebsite: string | null;
}

export const registryJurisdiction = {
  UK: 'UK',
  CH: 'CH',
} as const;

export type RegistryJurisdiction = keyof typeof registryJurisdiction;

export const registryCompanyStatus = {
  Active: 'Active',
  Dissolved: 'Dissolved',
  Liquidation: 'Liquidation',
  Receivership: 'Receivership',
  Administration: 'Administration',
  VoluntaryArrangement: 'VoluntaryArrangement',
  ConvertedClosed: 'ConvertedClosed',
  Removed: 'Removed',
  Unknown: 'Unknown',
} as const;

export type RegistryCompanyStatus = keyof typeof registryCompanyStatus;

export interface RegistryAddressDto {
  line1: string | null;
  line2: string | null;
  locality: string | null;
  region: string | null;
  postalCode: string | null;
  country: string | null;
}

export interface RegistryCompanyDto {
  name: string;
  number: string;
  status: RegistryCompanyStatus;
  statusDetail: string | null;
  legalForm: string | null;
  subJurisdiction: string | null;
  formationDate: string | null;
  cessationDate: string | null;
  registeredAddress: RegistryAddressDto | null;
}

export interface RegistryDirectorDto {
  name: string;
  role: string;
  appointedOn: string | null;
  dateOfBirth: { month: number; year: number } | null;
  nationality: string | null;
  countryOfResidence: string | null;
}

export interface CheckRegistryBody {
  jurisdiction: RegistryJurisdiction;
  companyNumber: string;
}

export interface CheckRegistryResponse {
  found: boolean;
  jurisdiction: RegistryJurisdiction;
  sourceEtag: string | null;
  company: RegistryCompanyDto | null;
  directors: RegistryDirectorDto[];
  fieldsNotProvidedByRegistry: string[];
}
